import { useEffect, useState } from 'react';

/**
 * Whether a horizontal row is close enough to the lobby viewport to be worth filling.
 *
 * A row's own virtualizer only knows its horizontal scroll position, so a section sitting
 * ten screens away still renders a full window of cards - and sections are never unmounted
 * once built. That makes the mounted set grow with the number of sections rather than with
 * what is on screen, and each card holds a decoded thumbnail costing megabytes.
 *
 * Emptying a row has no layout consequences by construction: its scroller has a fixed
 * height from the card dimensions, and the inner spacer takes its width from the
 * virtualizer's total size, so neither the row's height nor its horizontal scroll range
 * depends on how many cards are rendered. Nothing moves, and scrolling back refills at the
 * same offsets.
 */

/**
 * Root heights of travel a row gets before it lets its cards go - and, in the other
 * direction, how far ahead it refills. The whole memory/latency dial.
 *
 * Split by direction because the two sides are not the same problem. A row above has been
 * on screen already, so its thumbnails are in the browser's cache and refilling it is a
 * decode; a row below may never have been seen, and refilling it can mean the network. So
 * the expensive side is the one that needs the lead time, and the cheap side is where a
 * wider margin is close to free - which is lucky, because scrolling back up is where a
 * late refill is most obvious: the user knows what was there.
 *
 * Both are safe to raise: a row that is actually visible intersects the root itself, so
 * widening either margin can never blank something on screen. The only cost is memory.
 */
const KEEP_CARDS_VIEWPORTS_ABOVE = 3;
const KEEP_CARDS_VIEWPORTS_BELOW = 1.5;

const SCROLLER_SELECTOR = '[data-section-container]';

/**
 * Returns `true` - fill the row - until an enclosing lobby scroller says otherwise, so a
 * row rendered outside one (search results, campaign grids, Storybook) always fills.
 *
 * `resetKey` re-resolves the root and re-measures the margin; pass something that changes
 * with the viewport.
 */
export function useIsRowNearViewport(
  elementRef: React.RefObject<HTMLElement | null>,
  resetKey?: string | number,
): boolean {
  const [isNear, setIsNear] = useState(true);

  useEffect(() => {
    const element = elementRef.current;
    const root = element?.closest(SCROLLER_SELECTOR);
    if (!element || !root) return undefined;

    // Resolved to pixels against the scroller rather than passed as a percentage, so the
    // window is identical on every engine. A scroller still waiting for layout would give
    // a zero margin - which reads as "keep only what is on screen" - so the viewport
    // stands in until it settles. A margin left stale by a rotation is harmless either
    // way: a visible row intersects the root itself and can never be reported as far.
    const rootHeight = root.clientHeight || window.innerHeight;
    // top right bottom left - "above" is the top margin, since a row approaching from
    // above crosses the root's top edge.
    const rootMargin = [
      `${Math.round(rootHeight * KEEP_CARDS_VIEWPORTS_ABOVE)}px`,
      '0px',
      `${Math.round(rootHeight * KEEP_CARDS_VIEWPORTS_BELOW)}px`,
      '0px',
    ].join(' ');

    const observer = new IntersectionObserver(
      ([entry]) => setIsNear(!!entry?.isIntersecting),
      { root, rootMargin, threshold: 0 },
    );
    observer.observe(element);

    return () => observer.disconnect();
  }, [elementRef, resetKey]);

  return isNear;
}
